/**
 * Typed User Settings Keys
 *
 * This file defines all possible user setting keys with their types.
 * Use these constants to ensure type safety and prevent typos.
 */

export const USER_SETTING_KEYS = {
    TABLE_COLUMNS: (tableKey: string) => `table.${tableKey}.columns` as const,
    TABLE_COLUMN_ORDER: (tableKey: string) => `table.${tableKey}.columnOrder` as const,
    TABLE_COLUMN_ORDER_VERSION: (tableKey: string) => `table.${tableKey}.columnOrderVersion` as const,
    TABLE_PAGE_SIZE: (tableKey: string) => `table.${tableKey}.pageSize` as const,
    /** Which complaints dashboard a user prefers — the new ('v2') or legacy view. */
    COMPLAINTS_DASHBOARD_VIEW: 'dashboard.complaints.view',
    /** New left navigation (LeftNavV2): the per-user opt-in. */
    NAV_USE_NEW_LEFT_NAV: 'navigation.useNewLeftNav',
    /** New left navigation: pinned favourite destinations (route templates, pin order preserved). */
    NAV_FAVOURITE_PATHS: 'navigation.favouritePaths',
    /** What's new: release ids the person has READ (engaged with), mapped to the ISO time. */
    WHATS_NEW_SEEN: 'whatsNew.seen',
    /** What's new: release ids that have auto-opened on arrival for this person, mapped to the ISO time. */
    WHATS_NEW_ARRIVED: 'whatsNew.arrived',
} as const;

export interface UserSettingValues {
    [key: `table.${string}.columns`]: string[];
    [key: `table.${string}.columnOrder`]: string[];
    [key: `table.${string}.columnOrderVersion`]: number;
    [key: `table.${string}.pageSize`]: number;
    'dashboard.complaints.view': 'v2' | 'legacy';
    'navigation.useNewLeftNav': boolean;
    'navigation.favouritePaths': string[];
    'whatsNew.seen': Record<string, string>;
    'whatsNew.arrived': Record<string, string>;
}

export type UserSettingKey = keyof UserSettingValues;

export type UserSettingValue<K extends UserSettingKey> = UserSettingValues[K];

export interface TableSettings {
    columns?: string[];
    columnOrder?: string[];
    columnOrderVersion?: number;
    pageSize?: number;
}

export const getTableSettingKey = {
    columns: (tableKey: string) => USER_SETTING_KEYS.TABLE_COLUMNS(tableKey),
    columnOrder: (tableKey: string) => USER_SETTING_KEYS.TABLE_COLUMN_ORDER(tableKey),
    columnOrderVersion: (tableKey: string) => USER_SETTING_KEYS.TABLE_COLUMN_ORDER_VERSION(tableKey),
    pageSize: (tableKey: string) => USER_SETTING_KEYS.TABLE_PAGE_SIZE(tableKey),
} as const;
